
import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      try {
        setScrollY(window.scrollY ?? 0);
      } catch (err) {
        console.error('Failed to read scroll position:', err);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax background image */}
      <div
        className="absolute inset-0 will-change-transform"
        style={{
          transform: `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0003})`,
        }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/4fe7e362c09238b378582f8193382d8c.jpg"
          alt="Background"
          className="w-full h-[120%] object-cover object-top"
        />
      </div>

      {/* Dark overlay with gradient */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/60 to-dark"
        style={{
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      ></div>

      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center pt-20 sm:pt-24">
        {/* Tag line */}
        <div
          className={`transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 text-primary text-xs sm:text-sm font-semibold rounded-full mb-4 sm:mb-6 whitespace-nowrap">
            <i className="ri-robot-line mr-2"></i>
            Automação Inteligente de Atendimento
          </span>
        </div>

        {/* Main heading */}
        <h1
          className={`text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-6 sm:mb-8 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '150ms' }}
        >
          Sua Inteligência Artificial que{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan">atende e vende</span> por você{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">24h por dia</span>.
        </h1>

        {/* Sub‑title */}
        <p
          className={`text-sm sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed transition-all duration-1000 ease-out px-2 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '300ms' }}
        >
          Automatize seu atendimento, elimine o risco de perder clientes por demora ou
          esquecimento e transforme seu WhatsApp em uma máquina organizada de vendas.
        </p>

        {/* Call‑to‑action buttons */}
        <div
          className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center transition-all duration-1000 ease-out px-4 sm:px-0 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '450ms' }}
        >
          <a
            href="https://pay.kiwify.com.br/rnRjiX5"
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-light hover:to-primary text-dark font-bold rounded-lg shadow-lg shadow-primary/30 transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-center text-sm sm:text-base"
          >
            Quero Automatizar Meu Atendimento
          </a>
          <a
            href="#planos"
            className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-secondary/50 hover:border-secondary text-white hover:text-secondary-light text-sm sm:text-base font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer text-center"
          >
            Ver Planos
          </a>
        </div>

        {/* Social icons */}
        <div
          className={`mt-10 sm:mt-16 flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-400 transition-all duration-1000 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-dark-secondary/50 rounded-full border border-dark-border">
            <i className="ri-whatsapp-line text-lg sm:text-xl text-green-400"></i>
            <span className="whitespace-nowrap">WhatsApp</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-dark-secondary/50 rounded-full border border-dark-border">
            <i className="ri-instagram-line text-lg sm:text-xl text-pink-400"></i>
            <span className="whitespace-nowrap">Instagram</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-dark-secondary/50 rounded-full border border-dark-border">
            <i className="ri-messenger-line text-lg sm:text-xl text-accent-cyan"></i>
            <span className="whitespace-nowrap">Messenger</span>
          </div>
        </div>
      </div>

      {/* Down‑arrow cue */}
      <div
        className={`absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDelay: '800ms' }}
      >
        <a href="#problema" className="flex flex-col items-center text-gray-400 hover:text-primary transition-colors cursor-pointer">
          <span className="text-xs sm:text-sm mb-2 whitespace-nowrap">Saiba mais</span>
          <i className="ri-arrow-down-line text-xl sm:text-2xl animate-bounce"></i>
        </a>
      </div>
    </section>
  );
}
